import { compact, defaultsDeep, uniq } from 'lodash';
import { RegisterUserPayload, RegistrationType, User, UserType } from '@/types/index';

const userTypeByRegistrationType: [
  userType: UserType,
  registrationType: RegistrationType,
][] = [
  [UserType.PressArea, RegistrationType.PressArea],
  [UserType.Library, RegistrationType.Library],
  [UserType.Single, RegistrationType.Quotation],
];

type UserDetails = User['apps'][number]['modules'][number]['jsonData'];

export const isWorkplaceUser = (user: User | undefined) => {
  const app = user?.apps?.find((app) => app.name === 'CUSTOMERS_PORTAL');
  return app?.modules?.some((module) => module.name === 'WORKPLACE');
};

export const getUserDetails = (user: User | undefined): UserDetails | undefined => {
  const app = user?.apps?.find((app) => app.name === 'CUSTOMERS_PORTAL');
  return app?.modules?.find((module) => module.name === 'WORKPLACE')?.jsonData;
};

export const isOfType = (user: User, userType: UserType) =>
  getUserTypes(user).includes(userType);

const getUserTypes = (user: User): UserType[] => {
  const registrationTypes = getUserDetails(user)?.registrationType as
    | RegistrationType[]
    | undefined;

  return compact(
    userTypeByRegistrationType
      .filter(([_, registrationType]) => registrationTypes?.includes(registrationType))
      .map(([userType]) => userType)
  );
};

export const getUserType = (user: User): UserType =>
  getUserTypes(user)[0] ?? UserType.Single;

const getRegistrationTypes = (userType: UserType): RegistrationType[] => {
  // All users need to be at least of Quotation type
  const generic = RegistrationType.Quotation;
  const specific = userTypeByRegistrationType
    .filter(([type]) => type === userType)
    .map(([, registrationType]) => registrationType);
  return uniq([generic, ...specific]);
};

export const getPersonalInformation = (user: User) => {
  return {
    name: user.name,
    lastName: user.lastName,
    userName: user.userName,
    email: user.email,
    phoneNumber: user.phoneNumber,
    web: getUserDetails(user)?.web || '',
  };
};

export const getAddress = (user: User) => {
  const address = getUserDetails(user)?.address;

  return {
    address_streetAddress: address?.streetAddress ?? '',
    address_city: address?.city ?? '',
    address_country: address?.country ?? '',
    address_postCode: address?.postCode ?? '',
    address_stateOrProvince: address?.stateOrProvince ?? '',
    address_phoneNumber: address?.phoneNumber ?? '',
  };
};

const getCompany = (user: User) => {
  const company = getUserDetails(user)?.company;

  return {
    company_name: company?.name ?? '',
    company_function: company?.function ?? '',
    company_activity: company?.activity ?? '',
    company_activityType: company?.activityType ?? '',
  };
};

const getDescription = (user: User) => {
  return {
    message: user?.message ?? '',
  };
};

const getPressAreaDetails = (user: User) => {
  return {
    media: getUserDetails(user)?.media,
    occupation: getUserDetails(user)?.occupation,
  };
};

export type NormalizedValues = {
  name: string;
  lastName: string;
  message?: string;
  web?: string;
  email: string;
  address_streetAddress: string;
  address_city: string;
  address_country: string;
  address_postCode: string;
  address_stateOrProvince: string;
  address_phoneNumber: string;
  company_name: string;
  company_function?: string;
  company_activity?: string;
  company_activityType?: string;
  media?: string;
  occupation?: string;
};

export const getUserFormValues = (user?: User): NormalizedValues =>
  user
    ? {
        ...getPersonalInformation(user),
        ...getAddress(user),
        ...getCompany(user),
        ...getDescription(user),
        ...getPressAreaDetails(user),
      }
    : {
        name: '',
        lastName: '',
        email: '',
        message: '',
        web: getUserDetails(user)?.web || '',
        address_streetAddress: '',
        address_city: '',
        address_country: '',
        address_postCode: '',
        address_stateOrProvince: '',
        address_phoneNumber: '',
        company_name: '',
        company_function: '',
        company_activity: '',
        company_activityType: '',
        media: undefined,
        occupation: undefined,
      };

export const getCreatePayload = (
  values: ReturnType<typeof getUserFormValues>,
  userType: UserType,
  languageId: string
): RegisterUserPayload => ({
  store: 'workplace',
  email: values.email,
  registrationType: getRegistrationTypes(userType),
  name: values.name,
  firstName: values.name,
  lastName: values.lastName,
  phoneNumber: values.address_phoneNumber,
  descriptionOfYourRequest: values.message,
  languageId,
  address: {
    streetAddress: values.address_streetAddress,
    city: values.address_city,
    country: values.address_country,
    postCode: values.address_postCode,
    stateOrProvince: values.address_stateOrProvince,
    phoneNumber: values.address_phoneNumber,
  },
  web: values.web,
  ...(userType === UserType.PressArea
    ? {
        media: values.media,
        occupation: values.occupation,
      }
    : {
        company: {
          name: values.company_name,
          function: values.company_function,
          activity: values.company_activity,
          activityType: values.company_activityType,
        },
      }),
});

export const getUpdatePayload = (
  user: User,
  values: ReturnType<typeof getUserFormValues>
): Partial<User> => {
  const apps = [
    ...user.apps.map((app) => ({
      ...app,
      modules: app.modules.map((module) =>
        module.name === 'WORKPLACE'
          ? {
              ...module,
              jsonData: {
                ...module.jsonData,
                web: values.web,
                address: {
                  ...module.jsonData.address,
                  streetAddress: values.address_streetAddress,
                  city: values.address_city,
                  country: values.address_country,
                  postCode: values.address_postCode,
                  stateOrProvince: values.address_stateOrProvince,
                  phoneNumber: values.address_phoneNumber,
                },
                company: {
                  ...module.jsonData.company,
                  name: values.company_name,
                  function: values.company_function,
                  activity: values.company_activity,
                  activityType: values.company_activityType,
                },
                media: values.media,
                occupation: values.occupation,
              },
            }
          : module
      ),
    })),
  ];
  return {
    languageId: user.languageId,
    userName: user.userName,
    email: user.email,
    name: values.name,
    lastName: values.lastName,
    phoneNumber: values.address_phoneNumber,
    apps,
  };
};

export const getUpgradePayload = (
  user: User,
  values: ReturnType<typeof getUserFormValues>,
  upgradeTo: UserType
) => {
  const payload = [...getUserTypes(user), upgradeTo].reduce(
    (accum, type) => defaultsDeep(getCreatePayload(values, type, user.languageId), accum),
    {}
  );

  const currentRegistrationTypes = getUserTypes(user).map(getRegistrationTypes).flat();
  return {
    ...payload,
    registrationType: uniq([
      ...currentRegistrationTypes,
      ...getRegistrationTypes(upgradeTo),
    ]),
  };
};
